import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Plus, Target, Edit, Trash2, Eye, Filter, ArrowUp, ArrowDown, BarChart3, Maximize, Minimize, Zap, CheckCircle, Clock } from 'lucide-react';
import type { AppDispatch, RootState } from '@/store';
import { usePermissions } from '@/hooks/usePermissions';
import { Permission } from '@/utils/permissions';
import { PermissionGuard } from '@/components/auth/PermissionGuard';
import {
  fetchEpics,
  fetchEpicById,
  createEpic,
  updateEpic,
  deleteEpic,
  setFilters,
  clearError,
  setSorting
} from '@/store/slices/epicsSlice';
import { fetchInvestibleStructures } from '@/store/slices/investibleStructuresSlice';
import { fetchPrograms } from '@/store/slices/programsSlice';
import { DataTable } from '@/components/ui';
import {
  GlassCard,
  GlassButton,
  GlassBadge,
  GlassSelect,
  GlassPageHeader,
  GlassModal,
  GlassConfirmModal,
  GlassLoadingSpinner,
  GlassAlert,
  SectionReveal,
  MetricCard,
  CollapsibleMetricsSection,
} from '@/design-system';
import EpicForm from './components/EpicForm';
import EpicDetailModal from './components/EpicDetailModal';
import { EpicPriorityManager } from './components/EpicPriorityManager';
import type { Epic, EpicStatus, Column } from '@/types';
import { EpicStatus as EpicStatusEnum } from '@/types';
import { formatCurrencyCompact } from '@/utils/formatters';

const EpicsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { can } = usePermissions();
  const {
    epics,
    isLoading,
    isCreating,
    isUpdating,
    isDeleting,
    error,
    filters,
    sortBy,
    sortOrder,
  } = useSelector((state: RootState) => state.epics);

  const { structures } = useSelector((state: RootState) => state.investibleStructures || { structures: [] } as any);
  const { programs } = useSelector((state: RootState) => state.programs || { programs: [] } as any);
  const { user } = useSelector((state: RootState) => state.auth);

  // Check if user is a Program Lead
  const isProgramLead = user?.role === 'PROGRAM_LEAD';

  // Find the program led by the current user (only for PROGRAM_LEAD role)
  const userLedProgram = useMemo(() => {
    if (!isProgramLead || !user?.id || !programs.length) return null;
    return programs.find((p: any) => p.leadId === user.id || p.lead?.id === user.id);
  }, [isProgramLead, user?.id, programs]);


  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
const [isPriorityManagerOpen, setIsPriorityManagerOpen] = useState(false);
const [selectedEpic, setSelectedEpic] = useState<Epic | null>(null);
const [showFilters, setShowFilters] = useState(false);
const [detailModalInitialTab, setDetailModalInitialTab] = useState('overview');
const [isMaximized, setIsMaximized] = useState(false);
const totalRequested = epics.reduce((sum, epic) => sum + epic.requestedTeamWeeks, 0);
const totalAllocated = epics.reduce((sum, epic) => sum + epic.allocatedTeamWeeks, 0);
const remaining = totalRequested - totalAllocated;
const allocationPercent = totalRequested > 0 ? Math.min((totalAllocated / totalRequested) * 100, 100) : 0;

  useEffect(() => {
    const apiFilters: any = { ...filters };
    // Program Leads always see only their program's EPICs (backend enforces this too)
    if (isProgramLead && userLedProgram?.id) {
      apiFilters.supplyingProgramId = userLedProgram.id;
    }
    dispatch(fetchEpics(apiFilters));
    dispatch(fetchInvestibleStructures({}));
    dispatch(fetchPrograms({}));
  }, [dispatch, filters, userLedProgram?.id, isProgramLead]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        dispatch(clearError());
      }, 5000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [error, dispatch]);

  const handleCreateEpic = async (data: Partial<Epic>) => {
    const result = await dispatch(createEpic(data));
    if (createEpic.fulfilled.match(result)) {
      setIsCreateModalOpen(false);
    }
  };

  const handleUpdateEpic = async (data: Partial<Epic>) => {
    if (!selectedEpic) return;
    const result = await dispatch(updateEpic({ id: selectedEpic.id, data }));
    if (updateEpic.fulfilled.match(result)) {
      setIsEditModalOpen(false);
      setSelectedEpic(null);
    }
  };

  const handleDeleteEpic = async () => {
    if (!selectedEpic) return;
    const result = await dispatch(deleteEpic(selectedEpic.id));
    if (deleteEpic.fulfilled.match(result)) {
      setIsDeleteModalOpen(false);
      setSelectedEpic(null);
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    const newFilters = { ...filters };
    if (value) {
      newFilters[key] = value;
    } else {
      delete newFilters[key];
    }
    dispatch(setFilters(newFilters));
  };

  const handleSortChange = (field: string) => {
    const newSortOrder = sortBy === field && sortOrder === 'asc' ? 'desc' : 'asc';
    dispatch(setSorting({ sortBy: field as any, sortOrder: newSortOrder }));
  };

  // Helper function to fetch full epic details and open modal
  const handleViewEpicDetails = async (epic: Epic, initialTab: 'overview' | 'features' | 'allocations' | 'timeline' = 'overview') => {
    try {
      const result = await dispatch(fetchEpicById(epic.id));
      if (fetchEpicById.fulfilled.match(result)) {
        setSelectedEpic(result.payload as Epic);
        setDetailModalInitialTab(initialTab);
        setIsDetailModalOpen(true);
      }
    } catch (error) {
      console.error('Failed to fetch epic details:', error);
    }
  };

  const getStatusBadgeProps = (status: EpicStatus): { variant?: 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'info'; status?: 'committed' | 'stretch' | 'draft' | 'prioritized' } => {
    switch (status) {
      case EpicStatusEnum.COMMITTED:
        return { status: 'committed' };
      case EpicStatusEnum.STRETCH:
        return { status: 'stretch' };
      case EpicStatusEnum.DELIVERED:
        return { variant: 'success' };
      case EpicStatusEnum.CANCELLED:
        return { variant: 'danger' };
      case EpicStatusEnum.PRIORITIZED:
        return { status: 'prioritized' };
      case EpicStatusEnum.DRAFT:
        return { status: 'draft' };
      case EpicStatusEnum.SUBMITTED:
        return { variant: 'info' };
      default:
        return { variant: 'default' };
    }
  };

  const structureOptions = [
    { value: '', label: 'All Structures' },
    ...structures.map(structure => ({
      value: structure.id,
      label: structure.name
    }))
  ];

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'DRAFT', label: 'Draft' },
    { value: 'SUBMITTED', label: 'Submitted' },
    { value: 'PRIORITIZED', label: 'Prioritized' },
    { value: 'COMMITTED', label: 'Committed' },
    { value: 'STRETCH', label: 'Stretch' },
    { value: 'DELIVERED', label: 'Delivered' },
    { value: 'CANCELLED', label: 'Cancelled' }
  ];

  const programOptions = [
    { value: '', label: 'All Programs' },
    ...programs.map(program => ({
      value: program.id,
      label: program.name
    }))
  ];

  // Table columns for DataTable view
  const columns: Column<Epic>[] = [
    {
      key: 'priority',
      header: 'Priority',
      sortable: true,
      width: '80px',
      render: (value) => (
        <div className="flex items-center justify-center">
          <span className="text-sm font-medium text-[var(--text)]">
            {value || '-'}
          </span>
        </div>
      )
    },
    {
      key: 'name',
      header: 'EPIC Name',
      sortable: true,
      render: (value, row) => (
        <div>
          <div className="flex items-center gap-2">
            <span className="font-medium text-[var(--text)]">{value}</span>
          </div>
        </div>
      )
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (value) => (
        <GlassBadge {...getStatusBadgeProps(value)}>
          {value}
        </GlassBadge>
      )
    },
    {
      key: 'investibleStructure',
      header: 'Structure',
      render: (value) => value?.name || 'No Structure'
    },
    {
      key: 'investibleStructureOwner',
      header: 'IS Owner',
      render: (_value, row: any) => {
        const owner = row.investibleStructure?.owner;
        return owner ? `${owner.firstName} ${owner.lastName}` : 'Unassigned';
      }
    },
    {
      key: 'program',
      header: 'Program',
      render: (value) => value?.name || 'Unassigned'
    },
    {
      key: 'allocatedCost',
      header: 'Allocated Cost',
      render: (_value, row: any) => {
        const cost = row.allocatedCost ?? row.allocatedTeamWeeks * 89 * 40;
        return (
          <span className="text-status-success font-medium">
            {formatCurrencyCompact(cost, { fallback: '$0' })}
          </span>
        );
      }
    },
    {
      key: 'requestedTeamWeeks',
      header: 'Requested TWs',
      sortable: true,
      render: (value) => (
        <span className="text-[var(--accent)] font-medium">{value}</span>
      )
    },
    {
      key: 'allocatedTeamWeeks',
      header: 'Allocated TWs',
      sortable: true,
      render: (value, row) => (
        <div className="space-y-1">
          <span className="text-accent-violet font-medium">{value}</span>
          {row.requestedTeamWeeks > 0 && (
            <div className="w-full bg-[var(--surface-muted)] rounded-full h-1">
              <div
                className={`h-1 rounded-full ${
                  (value / row.requestedTeamWeeks) > 1 ? 'bg-status-error' :
                  (value / row.requestedTeamWeeks) >= 0.8 ? 'bg-status-success' : 'bg-accent-blue'
                }`}
                style={{ width: `${Math.min((value / row.requestedTeamWeeks) * 100, 100)}%` }}
              />
            </div>
          )}
        </div>
      )
    },
    {
      key: '_count',
      header: 'Features',
      render: (value) => value?.features || 0
    }
  ];

  const tableActions = [
    {
      label: 'View Details',
      icon: Eye,
      onClick: (epic: Epic) => {
        handleViewEpicDetails(epic, 'overview');
      }
    },
    {
      label: 'Manage Allocations',
      icon: BarChart3,
      onClick: (epic: Epic) => {
        handleViewEpicDetails(epic, 'allocations');
      }
    },
    ...(can(Permission.UPDATE_EPICS) || can(Permission.UPDATE_OWN_EPICS) ? [{
      label: 'Edit',
      icon: Edit,
      onClick: (epic: Epic) => {
        setSelectedEpic(epic);
        setIsEditModalOpen(true);
      }
    }] : []),
    ...(can(Permission.DELETE_EPICS) ? [{
      label: 'Delete',
      icon: Trash2,
      variant: 'danger' as const,
      onClick: (epic: Epic) => {
        setSelectedEpic(epic);
        setIsDeleteModalOpen(true);
      }
    }] : [])
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen glass-page-bg flex items-center justify-center">
        <GlassLoadingSpinner size="lg" variant="gradient" label="Loading EPICs..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen glass-page-bg p-6 lg:p-8">
      {!isMaximized && (
        <>
          <SectionReveal>
            <GlassPageHeader
              title="EPICs"
              subtitle="Manage strategic initiatives and priorities"
              icon={<Zap className="w-6 h-6" />}
              iconGradient="blue-violet"
              className="mb-6"
              actions={
                <div className="flex items-center gap-3">
                  {/* Show program badge for Program Leads */}
                  {isProgramLead && userLedProgram && (
                    <GlassBadge variant="info" size="sm">
                      {userLedProgram.name}
                    </GlassBadge>
                  )}
                  <div className="relative">
                    <GlassButton
                      variant="secondary"
                      size="sm"
                      onClick={() => setShowFilters(true)}
                      leftIcon={<Filter className="w-4 h-4" />}
                    >
                      Filters
                    </GlassButton>
                  </div>
                  {can(Permission.UPDATE_EPICS) && (
                    <>
                      <GlassButton
                        variant="secondary"
                        size="sm"
                        onClick={() => setIsPriorityManagerOpen(true)}
                        leftIcon={<BarChart3 className="w-4 h-4" />}
                        disabled={!filters.investibleStructureId}
                      >
                        Manage Priorities
                      </GlassButton>
                      <GlassButton
                        variant="secondary"
                        size="sm"
                        onClick={() => setIsMaximized(true)}
                        leftIcon={<Maximize className="w-4 h-4" />}
                      >
                        Maximize
                      </GlassButton>
                    </>
                  )}
                </div>
              }
            />
          </SectionReveal>

          {/* Metrics Row */}
          <SectionReveal delay={0.1}>
            <CollapsibleMetricsSection storageKey="epics">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <MetricCard
                  title="Total EPICs"
                  value={epics.length}
                  icon={<Zap className="w-5 h-5" />}
                  iconColor="violet"
                />
                <MetricCard
                  title="Requested TWs"
                  value={totalRequested}
                  icon={<Target className="w-5 h-5" />}
                  iconColor="blue"
                />
                <MetricCard
                  title="Allocated TWs"
                  value={totalAllocated}
                  icon={<CheckCircle className="w-5 h-5" />}
                  iconColor="emerald"
                  progress={allocationPercent}
                />
                <MetricCard
                  title="Remaining TWs"
                  value={remaining}
                  subtitle={remaining < 0 ? 'Over-allocated' : 'Available'}
                  icon={<Clock className="w-5 h-5" />}
                  iconColor={remaining < 0 ? 'rose' : 'cyan'}
                />
              </div>
            </CollapsibleMetricsSection>
          </SectionReveal>
        </>
      )}

      {!isMaximized && error && (
        <SectionReveal>
          <GlassAlert
            variant="error"
            message={error}
            onClose={() => dispatch(clearError())}
            className="mb-6"
          />
        </SectionReveal>
      )}

      {/* Filters Drawer */}
      {!isMaximized && (
        <div
          className={`fixed inset-0 z-40 transition ${showFilters ? 'pointer-events-auto' : 'pointer-events-none'}`}
          aria-hidden={!showFilters}
        >
          <div
            className={`absolute inset-0 bg-black/25 backdrop-blur-sm transition-opacity ${showFilters ? 'opacity-100' : 'opacity-0'}`}
            onClick={() => setShowFilters(false)}
          />
          <div
            className={`absolute right-0 top-0 h-full w-80 glass-strong border-l border-white/20 shadow-xl transition-transform duration-300 ${
              showFilters ? 'translate-x-0' : 'translate-x-full'
            }`}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
              <div>
                <h4 className="text-sm font-semibold text-[var(--text)]">Filters</h4>
                <p className="text-xs text-[var(--muted)]">Refine EPIC results</p>
              </div>
              <GlassButton variant="ghost" size="sm" onClick={() => setShowFilters(false)}>
                Close
              </GlassButton>
            </div>
            <div className="p-5 space-y-5">
              <div className="space-y-2">
                <span className="text-xs font-medium uppercase tracking-wide text-[var(--muted)]">Structure</span>
                <GlassSelect
                  value={filters.investibleStructureId || ''}
                  onChange={(value) => handleFilterChange('investibleStructureId', value)}
                  options={structureOptions}
                />
              </div>
              <div className="space-y-2">
                <span className="text-xs font-medium uppercase tracking-wide text-[var(--muted)]">Status</span>
                <GlassSelect
                  value={filters.status || ''}
                  onChange={(value) => handleFilterChange('status', value)}
                  options={statusOptions}
                />
              </div>
              <div className="space-y-2">
                <span className="text-xs font-medium uppercase tracking-wide text-[var(--muted)]">Program</span>
                <GlassSelect
                  value={filters.programId || ''}
                  onChange={(value) => handleFilterChange('programId', value)}
                  options={programOptions}
                />
              </div>
              <div className="flex gap-2 pt-2">
                <GlassButton
                  variant="secondary"
                  onClick={() => handleSortChange('priority')}
                  leftIcon={
                    sortBy === 'priority'
                      ? sortOrder === 'asc'
                        ? <ArrowUp className="w-3 h-3" />
                        : <ArrowDown className="w-3 h-3" />
                      : undefined
                  }
                  fullWidth
                >
                  Priority
                </GlassButton>
                <GlassButton
                  variant="ghost"
                  onClick={() => dispatch(setFilters({}))}
                  leftIcon={<Target className="w-4 h-4" />}
                  fullWidth
                >
                  Clear
                </GlassButton>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Maximized View - Outside SectionReveal to avoid stacking context */}
      {isMaximized && (
        <>
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[55]"
            onClick={() => setIsMaximized(false)}
          />
          <GlassCard
            variant="strong"
            className="fixed inset-4 z-[60] flex flex-col overflow-hidden"
            padding="none"
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 flex-shrink-0">
              <div>
                <h3 className="text-lg font-semibold text-[var(--text)]">EPICs</h3>
                <p className="text-sm text-[var(--muted)]">{epics.length} EPICs</p>
              </div>
              <GlassButton
                variant="secondary"
                size="sm"
                onClick={() => setIsMaximized(false)}
                leftIcon={<Minimize className="w-4 h-4" />}
              >
                Minimize
              </GlassButton>
            </div>
            <div className="flex-1 overflow-auto">
              <DataTable
                data={epics}
                columns={columns}
                actions={tableActions}
                onRowClick={(epic) => {
                  handleViewEpicDetails(epic, 'overview');
                }}
                emptyMessage="No EPICs found. Create a new EPIC to get started."
                pagination
                pageSize={50}
                sortable
              />
            </div>
          </GlassCard>
        </>
      )}

      {/* Normal Table View - Inside SectionReveal with scroll container */}
      {!isMaximized && (
        <SectionReveal delay={0.2}>
          <GlassCard variant="default" padding="none">
            <div className="max-h-[calc(100vh-350px)] overflow-auto">
              <DataTable
                data={epics}
                columns={columns}
                actions={tableActions}
                onRowClick={(epic) => {
                  handleViewEpicDetails(epic, 'overview');
                }}
                emptyMessage="No EPICs found. Create a new EPIC to get started."
                pagination
                pageSize={15}
                sortable
              />
            </div>
          </GlassCard>
        </SectionReveal>
      )}

      {/* Floating New EPIC button */}
      {!isMaximized && (
        <PermissionGuard permission={Permission.CREATE_EPICS}>
          <button
            className="fixed bottom-6 right-6 h-14 w-14 rounded-full shadow-lg bg-gradient-to-r from-accent-blue to-accent-violet text-white hover:shadow-xl hover:scale-105 transition-all duration-200 flex items-center justify-center"
            onClick={() => setIsCreateModalOpen(true)}
            aria-label="Create EPIC"
          >
            <Plus className="w-6 h-6" />
          </button>
        </PermissionGuard>
      )}

      {/* Create Modal */}
      <GlassModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Create EPIC"
        subtitle="Add a new strategic initiative"
        icon={<Zap className="w-5 h-5" />}
        iconGradient="blue-violet"
        size="xl"
      >
        <EpicForm
          structures={structures}
          programs={programs}
          onSubmit={handleCreateEpic}
          onCancel={() => setIsCreateModalOpen(false)}
          isLoading={isCreating}
        />
      </GlassModal>

      {/* Edit Modal */}
      <GlassModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedEpic(null);
        }}
        title="Edit EPIC"
        subtitle={selectedEpic?.name}
        icon={<Edit className="w-5 h-5" />}
        iconGradient="cyan"
        size="xl"
      >
        <EpicForm
          initialData={selectedEpic}
          structures={structures}
          programs={programs}
          onSubmit={handleUpdateEpic}
          onCancel={() => {
            setIsEditModalOpen(false);
            setSelectedEpic(null);
          }}
          isLoading={isUpdating}
        />
      </GlassModal>

      {/* Detail Modal */}
      <GlassModal
        isOpen={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedEpic(null);
          setDetailModalInitialTab('overview');
        }}
        title={selectedEpic?.name || 'EPIC Details'}
        subtitle="View details, features, allocations, and timeline"
        icon={<Eye className="w-5 h-5" />}
        iconGradient="blue-violet"
        size="xl"
      >
        {selectedEpic && (
          <EpicDetailModal
            epic={selectedEpic}
            initialTab={detailModalInitialTab}
            onClose={() => {
              setIsDetailModalOpen(false);
              setSelectedEpic(null);
              setDetailModalInitialTab('overview');
            }}
          />
        )}
      </GlassModal>

      {/* Delete Confirmation Modal */}
      <GlassConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedEpic(null);
        }}
        onConfirm={handleDeleteEpic}
        title="Delete EPIC"
        message={`Are you sure you want to delete "${selectedEpic?.name}"? This will also delete all associated features and cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        isLoading={isDeleting}
      />

      {/* Priority Manager Modal */}
      {filters.investibleStructureId && (
        <EpicPriorityManager
          investibleStructureId={filters.investibleStructureId}
          isOpen={isPriorityManagerOpen}
          onClose={() => setIsPriorityManagerOpen(false)}
        />
      )}
    </div>
  );
};

export default EpicsPage;
